import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUIStore } from '@/store/ui'
import { fetchGroups, fetchGroupSummary, updateGroup, deleteGroup } from '@/api/groups'
import { Avatar } from '@/components/ui/Avatar'
import { EmojiPickerModal } from '@/components/groups/EmojiPickerModal'
import { MembersModal } from '@/components/groups/MembersModal'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import { toFriend } from '@/lib/steam'
import type { Group } from '@/types'

interface GroupHeaderProps {
  sublabel?: string
}

export function GroupHeader({ sublabel }: GroupHeaderProps) {
  const navigate = useNavigate()
  const { activeGroupId, setActiveGroupId } = useUIStore()
  const queryClient = useQueryClient()
  const { data: groups = [] } = useQuery({ queryKey: ['groups'], queryFn: fetchGroups })
  const group = groups.find((g) => g.id === activeGroupId) ?? null

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const members = (summary?.members ?? []).map(toFriend)

  const { canManageGroup, canDeleteGroup } = useGroupPermissions()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const renameMutation = useMutation({
    mutationFn: (name: string) => updateGroup(group!.id, { name }),
    onMutate: async (name: string) => {
      await queryClient.cancelQueries({ queryKey: ['groups'] })
      const previous = queryClient.getQueryData<Group[]>(['groups'])
      queryClient.setQueryData<Group[]>(['groups'], (old) =>
        old?.map((g) => (g.id === group!.id ? { ...g, name } : g)) ?? old,
      )
      return { previous }
    },
    onError: (_err, _name, context) => {
      if (context?.previous) queryClient.setQueryData(['groups'], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteGroup(group!.id),
    onSuccess: () => {
      const deletedId = group!.id
      queryClient.setQueryData<Group[]>(['groups'], (old) =>
        old?.filter((g) => g.id !== deletedId) ?? old,
      )
      setActiveGroupId(null)
      setConfirmDelete(false)
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      navigate('/wishlist')
    },
  })

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [menuOpen])

  if (!group) return null

  function startEdit() {
    if (!canManageGroup) return
    setDraft(group!.name)
    setEditing(true)
  }

  function commit() {
    const trimmed = draft.trim()
    if (trimmed && trimmed !== group!.name) renameMutation.mutate(trimmed)
    setEditing(false)
  }

  function cancel() {
    setEditing(false)
  }

  return (
    <div className="cg-group-header">
      <div className="cg-group-title">
        {canManageGroup ? (
          <button
            type="button"
            className="cg-group-emoji cg-group-emoji-btn"
            title="Change icon"
            onClick={() => setShowEmojiPicker(true)}
          >
            {group.emoji}
          </button>
        ) : (
          <span className="cg-group-emoji">{group.emoji}</span>
        )}
        {showEmojiPicker && (
          <EmojiPickerModal
            groupId={group.id}
            currentEmoji={group.emoji}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
        <div className="cg-group-title-text">
          {editing ? (
            <input
              ref={inputRef}
              className="cg-group-title-input"
              value={draft}
              maxLength={80}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit()
                else if (e.key === 'Escape') cancel()
              }}
            />
          ) : (
            <h1
              className="cg-group-title-name"
              title={canManageGroup ? 'Rename group' : undefined}
              onClick={startEdit}
              style={canManageGroup ? undefined : { cursor: 'default' }}
            >
              {group.name}
            </h1>
          )}
          <span className="cg-group-count">
            {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
            {sublabel ? ` · ${sublabel}` : ''}
          </span>
        </div>
      </div>
      <div className="cg-group-header-right">
        <div className="cg-group-members">
          {members.map((m) => (
            <Avatar key={m.id} friend={m} size={28} />
          ))}
        </div>
        <div className="cg-group-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="cg-icon-btn"
            title="Group options"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="cg-group-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="cg-group-menu-item"
                onClick={() => {
                  setMenuOpen(false)
                  setShowMembers(true)
                }}
              >
                {canManageGroup ? 'Manage members' : 'Members'}
              </button>
              {canDeleteGroup && (
                <button
                  type="button"
                  role="menuitem"
                  className="cg-group-menu-item danger"
                  onClick={() => {
                    setMenuOpen(false)
                    setConfirmDelete(true)
                  }}
                >
                  Delete group
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {showMembers && (
        <MembersModal groupId={group.id} onClose={() => setShowMembers(false)} />
      )}
      {confirmDelete && createPortal(
        <div className="cg-modal-backdrop" onClick={() => !deleteMutation.isPending && setConfirmDelete(false)}>
          <div className="cg-modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="cg-modal-header">
              <h2>Delete group?</h2>
            </div>
            <div style={{ padding: '14px 18px', color: 'var(--fg-1)', fontSize: 13, lineHeight: 1.5 }}>
              <p style={{ margin: 0 }}>
                This will permanently delete <strong>{group.name}</strong> and remove its wishlist,
                playing, played, ratings, and votes for everyone. This can't be undone.
              </p>
            </div>
            <div className="cg-modal-footer">
              <button
                type="button"
                className="cg-btn"
                onClick={() => setConfirmDelete(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cg-btn danger"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete group'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
